import { Container } from '@acies/core';
import { InputField, InputFieldEvent, Label } from '@acies/ui';
import type { IGeometryMeasurement } from '../../../domain/geometry-shapes';
import GeometryMeasurementInputEvent from './geometry-measurement-input-event';

/**
 * One measurement of a shape: what it is called, above how long it is, for the user to give it another length.
 *
 * A Label over an InputField rather than a `FormInput`, because FormInput cannot be made numeric: it passes neither `type` nor `decimals` on to the field it holds. The field takes digits only, with no decimals and no minus, since a measurement is whole millimetres and one or more — the rule the domain decodes it by.
 *
 * A length the user gives it is answered with a {@link GeometryMeasurementInputEvent}; see #inputFieldBlurred for when.
 */
export default class GeometryMeasurementInput extends Container {
	/** What the measurement is called, as the user reads it. */
	public set text(value: string) {
		this.textLabel.text = value;
	}

	public get text(): string {
		return this.textLabel.text;
	}

	/** Which way the measurement runs, shown in the field beside its length. Null shows none. */
	public set icon(value: InputField['icon']) {
		this.inputField.icon = value;
	}

	public get icon(): InputField['icon'] {
		return this.inputField.icon;
	}

	/** Which measurement this is, which is what a length the user gives it is said to be the length of. Null says nothing. */
	public set measurement(value: IGeometryMeasurement | null) {
		this.#measurement = value;
	}

	public get measurement(): IGeometryMeasurement | null {
		return this.#measurement;
	}

	/**
	 * How long the measurement is, in whole millimetres.
	 *
	 * Kept here as the number it is, and written to the field as text. Read back from here rather than from the field, which reads its text off the input element and so has none to give before it is in the document.
	 *
	 * Not written to a field the user is typing in. The engine answers one change while the user is already at the next measurement, and every answer hands every measurement on show a length again: written, it would take what the user is typing from under them. Kept all the same, so leaving the field compares what was
	 * typed against the length the measurement has by then.
	 */
	public set value(value: number) {
		this.#value = value;
		// the answer to whatever was said last has arrived, or another position's length has: either
		// way it is this length a change is measured against from now on
		this.#said = null;
		if (!this.#typing) {
			this.inputField.text = String(value);
		}
	}

	public get value(): number | null {
		return this.#value;
	}

	#inputField!: InputField;
	#measurement: IGeometryMeasurement | null;
	/**
	 * The length last said, while the API has not yet answered it, or null when nothing is on its way.
	 *
	 * What a change is measured against while one is on its way, rather than {@link value}, which is still the length the measurement had before. The user typing that length back before the answer is a change — it takes back the one on its way — and measured against the old length it would look like none.
	 */
	#said: number | null;
	#textLabel!: Label;
	/** Whether the user has typed in the field since it was last left. See {@link value}. */
	#typing: boolean;
	#value: number | null;

	public constructor() {
		super();
		this.#measurement = null;
		this.#said = null;
		this.#typing = false;
		this.#value = null;
		this.width = 'fill';
		this.autoLayout = 'vertical';
		this.gapVertical = 4;
	}

	/** What the measurement is called, above the field, the way `LabelValueUnit` names its value. */
	public get textLabel(): Label {
		if (!this.#textLabel) {
			this.#textLabel = new Label();
			this.#textLabel.width = 'fill';
			this.#textLabel.size = 'medium';
			this.#textLabel.variant = 'light';
		}
		return this.#textLabel;
	}

	/** How long the measurement is. Right aligned, as the width and the height are where a position is made. */
	public get inputField(): InputField {
		if (!this.#inputField) {
			this.#inputField = new InputField();
			this.#inputField.width = 'fill';
			this.#inputField.type = 'numeric';
			this.#inputField.decimals = false;
			this.#inputField.negatives = false;
			// five digits is 99 999 millimetres, a hundred metres, and no window is near it
			this.#inputField.maximumCharacters = 5;
			this.#inputField.textAlign = 'right';
		}
		return this.#inputField;
	}

	/**
	 * Takes back the length the field said, after the API would not take it.
	 *
	 * Nothing is on its way any more, so the next change is measured against the measurement's own length again, and a field left without typing says nothing — see #said. Nothing is marked: what went wrong is said by the position's error card, which the dialog shows where the drawing was. And nothing is put back: what
	 * the user typed is still there to be corrected, and a refusal that quietly restored the old length would leave them wondering whether they had typed anything at all — the reason a refused answer is left in its row too.
	 */
	public rejectValue(): void {
		this.#said = null;
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponents([this.textLabel, this.inputField]);
		this.#addEventListeners();
	}

	/**
	 * Every event the field raises is caught here, and none of them leave.
	 *
	 * Two of them say something the measurement has to answer: CHANGED, which is the user typing, and BLURRED, which is the user done — Enter included, because Input blurs the field after raising ENTER. The rest say nothing anyone above can use. All of them are stopped either way, so a listener above hears about a
	 * measurement and never about the field it is typed in.
	 */
	#addEventListeners(): void {
		const stop = this.#stopEvent as EventListener;
		this.addEventListener(InputFieldEvent.BLURRED, this.#inputFieldBlurred as EventListener);
		this.addEventListener(InputFieldEvent.CHANGED, this.#inputFieldChanged as EventListener);
		this.addEventListener(InputFieldEvent.ENTER, stop);
		this.addEventListener(InputFieldEvent.FOCUSED, stop);
		this.addEventListener(InputFieldEvent.HOVERED, stop);
		this.addEventListener(InputFieldEvent.UN_HOVERED, stop);
	}

	/** Notes that the user is typing, so a length that arrives meanwhile leaves the field alone — see {@link value}. */
	#inputFieldChanged(event: InputFieldEvent): void {
		event.stopImmediatePropagation();
		this.#typing = true;
	}

	/**
	 * Answers a field the user has typed in and finished with, when it holds a length other than the measurement's.
	 *
	 * Only after typing. A field the user passed through without typing has said whatever it holds already, or holds the measurement's length; saying it again would ask the API once more for a length it refused, every time the user tabbed past it.
	 *
	 * Compared against the length last said while it is on its way, and else against the measurement's — not against what the field held when the user came to it, because that is what a change is measured against, and it may have moved while the user typed. Left with that length, the field changes nothing and says
	 * nothing.
	 *
	 * A field left with no length, or with nought, is given that length back and says nothing either: a measurement is a whole millimetre or more, so there is nothing that could be sent. Anything else the field can hold is one: digits only, and no more than five of them.
	 */
	#inputFieldBlurred(event: InputFieldEvent): void {
		event.stopImmediatePropagation();
		const typing = this.#typing;
		this.#typing = false;
		if (!typing || this.#measurement === null || this.#value === null) {
			return;
		}
		const measured = this.#said ?? this.#value;
		const typed = Number(this.inputField.text);
		if (typed < 1) {
			this.inputField.text = String(measured);
			return;
		}
		if (typed === measured) {
			return;
		}
		this.#said = typed;
		this.dispatchEvent(new GeometryMeasurementInputEvent(this.#measurement, typed));
	}

	/** Stops an event the field raised that says nothing outside the measurement. */
	#stopEvent(event: Event): void {
		event.stopImmediatePropagation();
	}
}
customElements.define('cf-geometry-measurement-input', GeometryMeasurementInput);
